<script lang="ts" setup>
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useRouter} from 'vue-router';
import {useInterventionLinks} from '@/ts/composables/stateful/use-links';
import {useIntervention} from '@/ts/composables/stateful/use-intervention';
import {useInterventionStore} from '@/ts/store/intervention-store';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {PublicationState} from '@/ts/types/dto/interventions-dto';
import NoClickBtn from '@/vue/molecules/no-click-btn.vue';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import InterventionLabel from '@/vue/molecules/intervention-label.vue';

const intervention = useInterventionStore();

const {hasContent} = useIntervention()!;
const api = useApi();
const router = useRouter();
const {aboutLink, sessionsLink, currentSessionLink, recapLink} = useInterventionLinks();
const [enroll, enrollingInProgress] = useLoadingNotification(
  async() => api.me.enrollIntervention(intervention.value!.id),
  (newIntervention) => {
    intervention.value = newIntervention;
    void router.push(currentSessionLink.value!);
  },
);
</script>
<template>
  <div class="intervention-details-page">
    <div class="header">
      <q-tabs class="tabs">
        <q-route-tab :to="aboutLink" :label="$t('about')"/>
        <q-route-tab v-if="hasContent" :to="sessionsLink" :label="$t('sessions')"/>
        <q-route-tab v-if="intervention!.publicationState === PublicationState.COMPLETED" :to="recapLink" :label="$t('recap')"/>
      </q-tabs>
    </div>

    <div class="menu">
      <intervention-label class="label" :publication-state="intervention!.publicationState"/>
      <div class="intervention-header">
        <lazy-image
          class="intervention-icon"
          :src="intervention!.image"
          :alt-txt="intervention!.name"
          :width="64"
          :height="64"
        />
        <h2 class="intervention-title text-display-3">
          {{ intervention!.name }}
        </h2>
        <div class="intervention-description">
          {{ intervention!.description }}
        </div>
      </div>
      <div class="q-mt-auto"/>
      <no-click-btn
        v-if="[PublicationState.COMPLETED, PublicationState.IN_PROGRESS].includes(intervention!.publicationState!)"
        class="registered"
        icon="check.svg"
        theme="p3-7"
        :title="intervention!.publicationState === PublicationState.IN_PROGRESS ? $t('registered') : $t('completed')"
      />
      <balm-btn
        v-else-if="hasContent"
        paddings="p3-7"
        theme="grey"
        class="enroll-btn"
        :label="intervention!.publicationState === PublicationState.WITHDREW ? $t('enroll_again') : $t('enroll')"
        :loading="enrollingInProgress"
        :disable="enrollingInProgress"
        @click="enroll"
      />
    </div>

    <div class="main">
      <router-view/>
    </div>
  </div>
</template>
<style lang="scss" scoped>

.tabs {
  @include tabs;
}

.intervention-details-page {
  display: grid;
  width: 100%;
  padding-left: var(--Spacing-spacing-6);
  padding-right: var(--Spacing-spacing-6);
  grid-template-rows: auto auto auto;
  grid-template-columns: 1fr;
  grid-template-areas:
  "menu"
  "header"
  "main";

  @include screen-lg {
    height: 100%;
    padding-left: 0;
    padding-right: 0;
    grid-template-columns: $menu-width 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas:
    "menu header"
    "menu main";
  }

  > .menu {
    height: 100%;
    grid-area: menu;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    padding-top: var(--Spacing-spacing-8);

    @include screen-lg {
      overflow-y: auto;
      padding: var(--Spacing-spacing-8) var(--Spacing-spacing-6) var(--Spacing-spacing-6) var(--Spacing-spacing-5);
    }
  }

  > .header {
    grid-area: header;
    padding: var(--Spacing-spacing-8) 0 var(--Spacing-spacing-2) 0;
    display: flex;

    @include screen-lg {
      padding: var(--Spacing-spacing-8) var(--Spacing-spacing-4) var(--Spacing-spacing-2) var(--Spacing-spacing-4);
    }
  }

  > .main {
    grid-area: main;

    // The bottom is what the scroll ends on. At zero the last row of whatever tab is open sits
    // against the edge of the window with nothing under it, which reads as content cut off rather
    // than content finished — the recap's session list is where it shows, being the longest.
    padding: var(--Spacing-spacing-4) 0 var(--Spacing-spacing-6) 0;
    min-height: 0;
    min-width: 0;

    @include screen-lg {
      overflow-y: auto;

      // Both sides, matching each other. With the right at zero this column alone ran to the
      // panel's edge while the menu and the header beside it kept their inset, so everything in
      // it — the tab content, its cards and the controls above them — ended flush against it.
      padding: var(--Spacing-spacing-4) var(--Spacing-spacing-6) var(--Spacing-spacing-6)
        var(--Spacing-spacing-6);
    }
  }
}

.enroll-btn {
  flex-shrink: 0; // if narrow screen height it will consume space from button

  @include screen-lg {
    width: 100%;
  }
}

.registered {
  @include screen-lg {
    margin: 0 auto;
    width: 100%;
  }
}

.menu {
  gap: var(--Spacing-spacing-2);

  @include screen-lg {
    gap: var(--Spacing-spacing-6);
  }
}

.intervention-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: var(--Spacing-spacing-4);
}

.intervention-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  margin-bottom: var(--Spacing-spacing-3);

  @include screen-lg {
    width: 64px;
    height: 64px;
    margin-bottom: var(--Spacing-spacing-5);
  }
}

.intervention-title {
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0 0 var(--Spacing-spacing-3) 0;

  @include screen-lg {
    margin-bottom: var(--Spacing-spacing-4);
  }
}

.intervention-description {
  @include screen-md {
    max-width: 400px;
  }
}

.header {
  gap: var(--Spacing-spacing-4);
  justify-content: space-between;
}

</style>
